import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ScriptInput.module.scss';
import * as FlagIcons from '../../../../components/FlagIcons/FlagIcons';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/Icon/Icon';
import { LANGUAGES } from '../../../../constants/projectCreatorConsts';
import cx from 'classnames';
import SelectOption from '../../../../components/SelectOption/SelectOption';
import VideoService from '../../../../service/VideoService';
import { CustomButton } from '../../../../components/Button/Button';
import DotsBtn from '../../../../components/Menu/DotsBtn';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import TextWithInfo from '../../../../components/TextWithInfo/TextWithInfo';
import Spinner from '../../../../../common/components/Spinner';
import { useUserContext } from '../../../../context/UserContext';
import { useHistory } from 'react-router-dom';
import { ToastNotification } from '../../../../components/Toast/Toast';
import Toast from '../../../../../common/utils/Toast';
import { toast } from 'react-toastify';

const MAX_DESCRIPTION_LENGTH = 2500;

const MIN_QUESTION_LENGTH = 3;
const MAX_QUESTION_LENGTH = 100;
const COMPANY_INIT_DESCRIPTIONS = [
  { language: LANGUAGES.ENGLISH.type, description: '' },
  { language: LANGUAGES.NEDERLANDS.type, description: '' },
]

const ScriptInput = ({ companyId }) => {
  const [descriptionsLanguage, setDescriptionsLanguage] = useState(LANGUAGES.ENGLISH);
  const [descriptions, setDescriptions] = useState(COMPANY_INIT_DESCRIPTIONS);

  const [isDescriptionsLoading, setIsDescriptionsLoading] = useState(false);
  const [isDescriptionsChanged, setIsDescriptionsChanged] = useState(false);

  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const [isCustomTemplatesLoading, setIsCustomTemplatesLoading] = useState(false);

  const [newTemplate, setNewTemplate] = useState({
    language: LANGUAGES.ENGLISH,
    videoType: {},
    categories: [],
  });
  const [isNewTemplateLoading, setIsNewTemplateLoading] = useState(false);

  const [updatedTemplate, setUpdatedTemplate] = useState({});

  const [editModeId, setEditModeId] = useState(null);
  const [showMenuId, setShowMenuId] = useState(null);

  const isTemplateQuestionsValid = useMemo(
    () => updatedTemplate.fields?.every(({ title }) => title?.trim()?.length > MIN_QUESTION_LENGTH),
    [updatedTemplate]
  );

  const currentDescriptionText = useMemo(
    () => descriptions.find(({ language }) => language === descriptionsLanguage.type)?.description || '',
    [descriptionsLanguage, descriptions]
  );

  const [touchedQuestions, setTouchedQuestions] = useState([]);

  const menuRef = useRef(null);
  const templatesRef = useRef(null);

  useOutsideClick(menuRef, () => {
    showMenuId && setShowMenuId(null);
  });

  const { userCompanyId, isStoryboardGeneratorEnabled } = useUserContext();

  const history = useHistory();

  const { t } = useTranslation();

  const handleSelectDescriptionsLanguage = value => {
    setDescriptionsLanguage(value);
  };

  const handleChangeDescriptions = event => {
    event.persist();

    setDescriptions(prevState =>
      prevState.map(({ language, description }) => {
        if (descriptionsLanguage?.type === language) {
          return { language, description: event?.target?.value?.trimStart() };
        }

        return { language, description };
      })
    );
    setIsDescriptionsChanged(true);
  };

  const handleSaveDescription = async () => {
    const loadingToast = ToastNotification({
      message: `${t('general.toast.company_description.saving')}`,
      spinner: true,
    });
    toast.update(loadingToast, { autoClose: 10000 });

    try {
      setIsDescriptionsChanged(false);

      const result = await VideoService.updateCompanyInfo(companyId ?? userCompanyId, { descriptions });
      if (result?.descriptions?.length) {
        setDescriptions(result.descriptions);
      }
      toast.dismiss(loadingToast);
      ToastNotification({
        message: t('general.toast.company_description.saving_successfully'),
        iconName: 'success',
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      Toast.error(t('general.toast.company_description.saving_error'));
    }
  };

  const handleChangeNewTemplate = (key, value) => {
    if (key === 'categories') {
      setNewTemplate(prevState => {
        const isCategoryAdded = prevState.categories?.some(category => category?.id === value?.id);

        if (isCategoryAdded) {
          // remove category
          return { ...prevState, [key]: prevState.categories?.filter(category => category?.id !== value?.id) || [] };
        }

        // add category
        return { ...prevState, [key]: [...(prevState.categories || []), value] };
      });

      return;
    }

    if (key === 'filteredCategories') {
      setNewTemplate(prevState => ({ ...prevState, categories: value }));
      return;
    }

    setNewTemplate(prevState => ({ ...prevState, [key]: value }));
  };

  const handleAddNewTemplate = async () => {
    const loadingToast = ToastNotification({
      message: `${t('general.toast.templates.creating')}`,
      spinner: true,
    });
    toast.update(loadingToast, { autoClose: 10000 });

    try {
      setIsNewTemplateLoading(true);

      const payload = {
        templateId: newTemplate.videoType.id,
        language: newTemplate.language.type,
        categories: newTemplate.categories?.map(({ id }) => id),
        // title: 'New template',
      };

      const response = await VideoService.createStoryboardTemplate(payload, companyId);

      if (response) {
        setCustomTemplates(prevState => [response, ...prevState]);
      }

      toast.dismiss(loadingToast);
      ToastNotification({
        message: t('general.toast.templates.creating_successfully'),
        iconName: 'success',
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      Toast.error(t('general.toast.templates.creating_error'));
    }

    setIsNewTemplateLoading(false);
  };

  const handleSaveUpdatedTemplate = async () => {
    if (!isTemplateQuestionsValid) {
      return;
    }

    const loadingToast = ToastNotification({
      message: `${t('general.toast.templates.saving')}`,
      spinner: true,
    });
    toast.update(loadingToast, { autoClose: 10000 });

    try {
      setEditModeId(null);

      const allQuestions = updatedTemplate.fields.map(({ title, id, isNew }, index) => {
        if (isNew) {
          return { title: title.trim(), sort: index };
        }

        return { title: title.trim(), id, sort: index };
      });

      const payload = {
        fields: allQuestions,
        categories: updatedTemplate.categories?.map(({ id }) => id),
        // title: 'New template',
      };

      const response = await VideoService.updateStoryboardTemplate(updatedTemplate.id, payload, companyId);
      if (response) {
        setCustomTemplates(prevState =>
          prevState.map(customTemplate => {
            if (customTemplate?.id === updatedTemplate.id) {
              return response;
            }

            return customTemplate;
          })
        );
      }

      toast.dismiss(loadingToast);
      ToastNotification({
        message: t('general.toast.templates.saving_successfully'),
        iconName: 'success',
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      Toast.error(t('general.toast.templates.saving_error'));
    }

    setTouchedQuestions([]);
  };

  const handleAddEditableTemplate = template => {
    setUpdatedTemplate(template);
    setEditModeId(template?.id);
    setShowMenuId(null);
  };

  const handleEditTemplate = (key, value) => {
    if (key === 'categories') {
      setUpdatedTemplate(prevState => {
        const isCategoryAdded = prevState.categories?.some(category => category?.id === value?.id);

        if (isCategoryAdded) {
          // remove category
          return { ...prevState, [key]: prevState.categories?.filter(category => category?.id !== value?.id) || [] };
        }

        // add category
        return { ...prevState, [key]: [...(prevState.categories || []), value] };
      });

      return;
    }

    if (key === 'newQuestion') {
      setUpdatedTemplate(prevState => ({ ...prevState, fields: [...prevState.fields, value] }));
      return;
    }

    if (key === 'removeQuestion') {
      setUpdatedTemplate(prevState => ({ ...prevState, fields: prevState.fields?.filter(({ id }) => id !== value) }));
      return;
    }

    if (key === 'editQuestion') {
      setUpdatedTemplate(prevState => {
        const editedQuestions = prevState.fields?.map(question => {
          if (question?.id === value?.id) {
            return { ...value, isNew: question.isNew };
          }

          return question;
        });

        return { ...prevState, fields: editedQuestions };
      });

      setTouchedQuestions(prevState => {
        if (prevState.includes(value?.id)) {
          return prevState;
        }

        return [...prevState, value?.id];
      });

      return;
    }
  };

  const resetTemplateUpdating = () => {
    setUpdatedTemplate({});
    setEditModeId(null);
    setTouchedQuestions([]);
  };

  const handleDuplicateTemplate = async template => {
    const loadingToast = ToastNotification({
      message: `${t('general.toast.templates.duplicating')}`,
      spinner: true,
    });
    toast.update(loadingToast, { autoClose: 10000 });

    try {
      setShowMenuId(null);

      const payload = {
        templateId: template.id,
        language: template.language,
        categories: template.categories?.map(({ id }) => id),
        // title: 'New template',
      };
      const response = await VideoService.createStoryboardTemplate(payload, companyId);
      if (response) {
        setCustomTemplates(prevState => [response, ...prevState]);
        templatesRef?.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
      }

      toast.dismiss(loadingToast);
      ToastNotification({
        message: t('general.toast.templates.duplicating_successfully'),
        iconName: 'success',
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      Toast.error(t('general.toast.templates.duplicating_error'));
    }
  };

  const handleDeleteTemplate = async templateId => {
    const isConfirm = confirm(t('general.toast.templates.deleting_confirm'));
    setShowMenuId(null);

    if (!isConfirm) {
      return;
    }

    const loadingToast = ToastNotification({
      message: `${t('general.toast.templates.deleting')}`,
      spinner: true,
    });
    toast.update(loadingToast, { autoClose: 10000 });

    try {
      await VideoService.removeStoryboardTemplate(templateId, companyId);
      setCustomTemplates(prevState => prevState.filter(({ id }) => id !== templateId));

      toast.dismiss(loadingToast);
      ToastNotification({
        message: t('general.toast.templates.deleting_successfully'),
        iconName: 'success',
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      Toast.error(t('general.toast.templates.deleting_error'));
    }
  };

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  useEffect(() => {
    const getDefaultTemplates = async () => {
      try {
        const templates = await VideoService.getDefaultStoryboardTemplates(newTemplate.language.type);

        if (templates?.length) {
          setDefaultTemplates(templates);
          handleChangeNewTemplate('videoType', templates[0]);
        } else {
          setDefaultTemplates([]);
          handleChangeNewTemplate('videoType', {});
        }
      } catch (err) {
        setDefaultTemplates([]);
        handleChangeNewTemplate('videoType', {});
        console.log(err);
      }
    };

    getDefaultTemplates();
  }, [newTemplate.language]);

  useEffect(() => {
    if (newTemplate.videoType) {
      handleChangeNewTemplate('filteredCategories', newTemplate.videoType.categories);
    }
  }, [newTemplate.videoType]);

  useEffect(() => {
    const getDescriptions = async () => {
      try {
        setIsDescriptionsLoading(true);
        const result = await VideoService.getCompanyInfo(companyId ?? userCompanyId);
        setDescriptions(result.descriptions?.length > 0 ? result.descriptions : COMPANY_INIT_DESCRIPTIONS);
      } catch (err) {
        console.log(err);
      }

      setIsDescriptionsLoading(false);
    };

    if (userCompanyId) {
      getDescriptions();
    }
  }, [userCompanyId, companyId]);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await VideoService.getStoryboardCategories();

        if (categories?.length) {
          setAvailableCategories(categories);
        } else {
          setAvailableCategories([]);
        }
      } catch (err) {
        console.log(err);
      }
    };

    getCategories();
  }, []);

  useEffect(() => {
    const getCustomTemplates = async () => {
      try {
        setIsCustomTemplatesLoading(true);
        const templates = await VideoService.getStoryboardTemplates({ isCustom: true }, companyId);

        if (templates?.length) {
          setCustomTemplates(templates);
        } else {
          setCustomTemplates([]);
        }
      } catch (err) {
        console.log(err);
      }

      setIsCustomTemplatesLoading(false);
    };

    getCustomTemplates();
  }, [companyId]);

  useEffect(() => {
    if (editModeId || isDescriptionsChanged) {
      window.addEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [editModeId, isDescriptionsChanged]);

  useEffect(() => {
    let unblock =
      editModeId || isDescriptionsChanged
        ? history.block(() => {
          // Navigation was blocked! Let's show a confirmation dialog
          // so the user can decide if they actually want to navigate
          // away and discard changes they've made in the current page.
          if (confirm(t('clip_organizer.confirm_leave_page'))) {
            // Unblock the navigation.
            return true;
          } else {
            return false;
          }
        })
        : history.block(() => true);

    return () => unblock();
  }, [editModeId, isDescriptionsChanged]);

  return (
    <div ref={templatesRef} className={styles.wrapper}>
      <div className={styles.descriptionWrapper}>
        <span className={styles.descriptionTitle}>{t('company.settings.script_input.company_description')}</span>
        <div className={styles.languagesContainer}>
          {Object.values(LANGUAGES).map(language => (
            <div
              key={language?.type}
              className={cx(styles.language, { [styles.selectedLanguage]: language.type === descriptionsLanguage.type })}
              onClick={() => handleSelectDescriptionsLanguage(language)}
            >
              <Icon src={language.src} className={styles.languageIcon} IconsSource={FlagIcons} />
            </div>
          ))}
        </div>
        {isDescriptionsLoading ? (
          <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
        ) : (
          <div className={styles.descriptionTextWrapper}>
            <div className={styles.descriptionCountWrapper}>
              <span className={styles.descriptionCount}>
                {currentDescriptionText?.length}/{MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
            <div className={styles.descriptionSaveContainer}>
              <textarea
                value={currentDescriptionText}
                onChange={handleChangeDescriptions}
                type="text"
                className={styles.descriptionText}
                placeholder={t('company.settings.script_input.description.placeholder')}
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
              <CustomButton
                label={t('general.save')}
                variant="primary"
                onClick={handleSaveDescription}
                className={styles.descriptionSaveBtn}
                loadingSpinner={true}
                loading={isDescriptionsLoading}
                disabled={!isDescriptionsChanged}
              />
            </div>
          </div>
        )}
      </div>

      {isStoryboardGeneratorEnabled && (
        <div className={styles.templatesContainer}>
          <span className={styles.templatesTitle}>{t('company.settings.script_input.types_of_videos')}</span>
          <div className={styles.templatesHeader}>
            <div className={styles.templateLanguageTitle}>{t('company.settings.script_input.languages')}</div>
            <div className={styles.templateVideoTypeTitle}>{t('company.settings.script_input.type_of_video')}</div>
            <div className={styles.templateCategoryTitle}>{t('company.settings.script_input.category')}</div>
          </div>
          <div className={styles.newTemplatesContainer}>
            <div className={styles.newTemplatesWrapper}>
              <SelectOption
                currentlySelected={newTemplate?.language}
                options={Object.values(LANGUAGES)}
                onSelect={value => handleChangeNewTemplate('language', value)}
                dropdownClassName={styles.customSelect}
                currentInputClassName={styles.templateLanguageSelect}
                selectedIcon="singleSelected"
                unselectedIcon="singleUnselected"
              />
              <SelectOption
                currentlySelected={newTemplate?.videoType}
                options={defaultTemplates}
                onSelect={value => handleChangeNewTemplate('videoType', value)}
                dropdownClassName={styles.customSelect}
                currentInputClassName={styles.templateVideoTypeSelect}
                selectedIcon="singleSelected"
                unselectedIcon="singleUnselected"
              />
              <SelectOption
                currentlySelected={newTemplate?.categories}
                options={availableCategories}
                onSelect={value => handleChangeNewTemplate('categories', value)}
                dropdownClassName={styles.customSelect}
                currentInputClassName={styles.templateCategorySelect}
                selectedIcon="multipleSelected"
                unselectedIcon="multipleUnselected"
                isMultiple={true}
              />
            </div>
            <CustomButton
              label={t('general.add')}
              variant="secondary"
              onClick={handleAddNewTemplate}
              className={styles.addButton}
              loadingSpinner={true}
              loading={isNewTemplateLoading}
              disabled={!newTemplate.videoType?.id}
            />
          </div>
        </div>
      )}

      {isStoryboardGeneratorEnabled && (
        <div>
          {isCustomTemplatesLoading ? (
            <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
          ) : (
            <div className={styles.customTemplatesContainer}>
              {customTemplates.map(customTemplate => (
                <div
                  key={customTemplate?.id}
                  className={cx(styles.customTemplateContainer, {
                    [styles.customTemplateContainerEditable]: editModeId === customTemplate?.id,
                  })}
                >
                  <div className={styles.customTemplateDetails}>
                    <div className={cx(styles.customTemplateInfo, { [styles.customTemplateEdit]: editModeId })}>
                      <span className={styles.templateLanguage}>
                        {t(Object.values(LANGUAGES).find(({ type }) => type === customTemplate.language)?.name)}
                      </span>
                      <span className={styles.templateVideoType}>{customTemplate.title}</span>
                      {editModeId === customTemplate?.id ? (
                        <SelectOption
                          currentlySelected={updatedTemplate?.categories}
                          options={availableCategories}
                          onSelect={value => handleEditTemplate('categories', value)}
                          dropdownClassName={styles.customSelect}
                          currentInputClassName={styles.templateCategorySelect}
                          selectedIcon="multipleSelected"
                          unselectedIcon="multipleUnselected"
                          isMultiple={true}
                        />
                      ) : (
                        <span className={styles.templateCategory}>
                          {customTemplate.categories?.map(category => t(category?.title))?.join(', ') || '-'}
                        </span>
                      )}
                    </div>
                    {editModeId === customTemplate?.id ? (
                      <div className={styles.saveTemplateContainer}>
                        <Icon
                          src="complete"
                          className={cx(styles.saveIcon, { [styles.saveIconDisabled]: !isTemplateQuestionsValid })}
                          onClick={handleSaveUpdatedTemplate}
                        />
                        <Icon src="close" className={styles.cancelIcon} onClick={resetTemplateUpdating} />
                      </div>
                    ) : (
                      <DotsBtn onClick={() => setShowMenuId(customTemplate?.id)} className={styles.moreBtn} />
                    )}
                    {showMenuId === customTemplate?.id && (
                      <div ref={menuRef} className={styles.menu}>
                        <div className={styles.menuItem} onClick={() => handleAddEditableTemplate(customTemplate)}>
                          {t('general.edit')}
                        </div>
                        <div className={styles.menuItem} onClick={() => handleDuplicateTemplate(customTemplate)}>
                          {t('general.video_action_button.duplicate')}
                        </div>
                        <div className={styles.menuItem} onClick={() => handleDeleteTemplate(customTemplate?.id)}>
                          {t('general.video_action_button.delete')}
                        </div>
                      </div>
                    )}
                  </div>
                  {editModeId === customTemplate?.id ? (
                    <div className={styles.customQuestionsContainer}>
                      <TextWithInfo
                        text={t('company.settings.script_input.questions')}
                        infoText={t('storyboard.generating.select_orientation.tooltip')}
                        className={styles.customQuestionHeader}
                      />
                      <div className={styles.customQuestionsListSelect}>
                        {updatedTemplate.fields.map((question, index, array) => (
                          <div key={question?.id} className={styles.editQuestionContainer}>
                            <span className={styles.customQuestionText}>{index + 1}.</span>
                            <input
                              className={cx(styles.inputQuestion, {
                                [styles.inputQuestionInvalid]:
                                  question.title?.trim()?.length <= MIN_QUESTION_LENGTH && touchedQuestions.includes(question?.id),
                              })}
                              type="text"
                              value={question.title}
                              onChange={event =>
                                handleEditTemplate('editQuestion', { title: event?.target.value?.trimStart(), id: question.id })
                              }
                              maxLength={MAX_QUESTION_LENGTH}
                            />
                            {array.length > 1 && (
                              <Icon
                                src="bin"
                                className={styles.binIcon}
                                onClick={() => handleEditTemplate('removeQuestion', question.id)}
                              />
                            )}
                          </div>
                        ))}
                        {updatedTemplate.fields?.length < 4 && (
                          <div
                            className={styles.addQuestionContainer}
                            onClick={() =>
                              handleEditTemplate('newQuestion', {
                                title: '',
                                id: updatedTemplate.fields?.length + 1 + Date.now(),
                                isNew: true,
                              })
                            }
                          >
                            <Icon src="plus" className={styles.plusIcon} />
                            <span className={styles.addQuestionText}>{t('company.settings.script_input.add_question')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.divider} />
                      <div className={styles.customQuestionsContainer}>
                        <span className={styles.customQuestionHeader}>{t('company.settings.script_input.questions')}</span>
                        <div className={styles.customQuestionsList}>
                          {customTemplate.fields.map((question, index) => (
                            <span key={question.id} className={styles.customQuestionText}>
                              {`${index + 1}. ${question.title}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptInput;
